import { useState, useRef, useEffect } from 'react';
import type { FillConfig } from '../../types';
import { defaultGradient } from '../../lib/elementDefaults';

const PRESET_COLORS = [
  '#ffffff', '#000000', '#ef4444', '#f97316', '#eab308',
  '#22c55e', '#06b6d4', '#3b82f6', '#0ea5e9', '#ec4899',
  '#f87171', '#fb923c', '#facc15', '#4ade80', '#22d3ee',
  '#60a5fa', '#38bdf8', '#f472b6', '#fca5a5', '#fdba74',
  '#fde047', '#86efac', '#67e8f9', '#93c5fd', '#7dd3fc',
  '#f9a8d4', '#1e293b', '#334155', '#475569', '#64748b',
  '#94a3b8', '#cbd5e1', '#e2e8f0', '#f1f5f9', 'transparent',
];

interface ColorPickerProps {
  fill: FillConfig;
  onChange: (fill: FillConfig) => void;
  showGradient?: boolean;
  label?: string;
  showOpacity?: boolean;
  opacity?: number;
  onOpacityChange?: (opacity: number) => void;
}

export default function ColorPicker({
  fill,
  onChange,
  showGradient = true,
  label,
  showOpacity = false,
  opacity = 1,
  onOpacityChange,
}: ColorPickerProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [hexInput, setHexInput] = useState(fill.color);
  const pickerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (pickerRef.current && !pickerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  useEffect(() => {
    setHexInput(fill.color);
  }, [fill.color]);

  const handleSolidColorChange = (color: string) => {
    setHexInput(color);
    onChange({ ...fill, type: 'solid', color });
  };

  const handleHexInputChange = (value: string) => {
    setHexInput(value);
    if (/^#[0-9a-fA-F]{6}$/.test(value) || /^#[0-9a-fA-F]{3}$/.test(value)) {
      onChange({ ...fill, type: 'solid', color: value });
    }
  };

  const handleGradientToggle = () => {
    if (fill.type === 'gradient') {
      onChange({ type: 'solid', color: fill.gradient?.stops[0]?.color || fill.color });
    } else {
      onChange({
        type: 'gradient',
        color: fill.color,
        gradient: fill.gradient || defaultGradient(fill.color, '#06b6d4'),
      });
    }
  };

  const handleGradientAngleChange = (angle: number) => {
    if (!fill.gradient) return;
    onChange({ ...fill, gradient: { ...fill.gradient, angle } });
  };

  const handleGradientStopColorChange = (index: number, color: string) => {
    if (!fill.gradient) return;
    const newStops = [...fill.gradient.stops];
    newStops[index] = { ...newStops[index], color };
    onChange({ ...fill, gradient: { ...fill.gradient, stops: newStops } });
  };

  const handleGradientStopOffsetChange = (index: number, offset: number) => {
    if (!fill.gradient) return;
    const newStops = [...fill.gradient.stops];
    newStops[index] = { ...newStops[index], offset: Math.max(0, Math.min(1, offset)) };
    onChange({ ...fill, gradient: { ...fill.gradient, stops: newStops } });
  };

  const addGradientStop = () => {
    if (!fill.gradient) return;
    const stops = fill.gradient.stops;
    const newOffset =
      stops.length > 1
        ? (stops[stops.length - 2].offset + stops[stops.length - 1].offset) / 2
        : 0.5;
    onChange({
      ...fill,
      gradient: {
        ...fill.gradient,
        stops: [...stops, { offset: newOffset, color: '#888888' }].sort(
          (a, b) => a.offset - b.offset
        ),
      },
    });
  };

  const removeGradientStop = (index: number) => {
    if (!fill.gradient || fill.gradient.stops.length <= 2) return;
    const newStops = fill.gradient.stops.filter((_, i) => i !== index);
    onChange({ ...fill, gradient: { ...fill.gradient, stops: newStops } });
  };

  const handleGradientTypeToggle = () => {
    if (!fill.gradient) return;
    onChange({
      ...fill,
      gradient: {
        ...fill.gradient,
        type: fill.gradient.type === 'linear' ? 'radial' : 'linear',
      },
    });
  };

  const previewBackground =
    fill.type === 'gradient' && fill.gradient
      ? fill.gradient.type === 'linear'
        ? `linear-gradient(${fill.gradient.angle}deg, ${fill.gradient.stops.map((s) => `${s.color} ${s.offset * 100}%`).join(', ')})`
        : `radial-gradient(circle, ${fill.gradient.stops.map((s) => `${s.color} ${s.offset * 100}%`).join(', ')})`
      : fill.color;

  return (
    <div className="relative" ref={pickerRef}>
      {label && (
        <label className="block text-[10px] uppercase tracking-wider text-white/30 mb-1.5 font-semibold">
          {label}
        </label>
      )}

      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 w-full px-2 py-1.5 bg-[#0a0a0a] border border-white/[0.06] rounded-lg hover:border-white/[0.12] transition-colors"
      >
        <div
          className="w-6 h-6 rounded border border-white/[0.1] flex-shrink-0"
          style={{
            background:
              fill.color === 'transparent'
                ? 'repeating-conic-gradient(#808080 0% 25%, transparent 0% 50%) 50% / 8px 8px'
                : previewBackground,
          }}
        />
        <span className="text-xs text-white/60 font-mono flex-1 text-left truncate">
          {fill.type === 'gradient' ? 'Gradient' : fill.color}
        </span>
        {fill.type === 'gradient' && (
          <span className="text-[9px] px-1.5 py-0.5 bg-cyan-500/20 text-cyan-400 rounded">
            {fill.gradient?.type === 'linear' ? 'LIN' : 'RAD'}
          </span>
        )}
      </button>

      {isOpen && (
        <div className="absolute z-50 top-full left-0 mt-1 w-64 bg-[#1a1a2e] border border-white/[0.1] rounded-lg shadow-2xl p-3">
          {showGradient && (
            <div className="flex gap-1 mb-3">
              <button
                type="button"
                onClick={() => fill.type !== 'solid' && handleGradientToggle()}
                className={`flex-1 text-[10px] py-1 rounded transition-colors ${
                  fill.type === 'solid'
                    ? 'bg-cyan-500/20 text-cyan-400 border border-cyan-500/30'
                    : 'bg-white/[0.03] text-white/40 border border-transparent hover:text-white/60'
                }`}
              >
                Solid
              </button>
              <button
                type="button"
                onClick={() => fill.type !== 'gradient' && handleGradientToggle()}
                className={`flex-1 text-[10px] py-1 rounded transition-colors ${
                  fill.type === 'gradient'
                    ? 'bg-cyan-500/20 text-cyan-400 border border-cyan-500/30'
                    : 'bg-white/[0.03] text-white/40 border border-transparent hover:text-white/60'
                }`}
              >
                Gradient
              </button>
            </div>
          )}

          {fill.type === 'solid' ? (
            <>
              <div className="flex items-center gap-2 mb-3">
                <input
                  type="color"
                  value={fill.color === 'transparent' ? '#000000' : fill.color}
                  onChange={(e) => handleSolidColorChange(e.target.value)}
                  className="w-8 h-8 rounded cursor-pointer border-0 bg-transparent"
                />
                <input
                  type="text"
                  value={hexInput}
                  onChange={(e) => handleHexInputChange(e.target.value)}
                  placeholder="#000000"
                  className="flex-1 px-2 py-1 text-xs font-mono bg-[#12121a] border border-white/[0.06] rounded text-white focus:outline-none focus:border-cyan-500/50"
                />
              </div>

              <div className="grid grid-cols-7 gap-1">
                {PRESET_COLORS.map((color) => (
                  <button
                    key={color}
                    type="button"
                    onClick={() =>
                      handleSolidColorChange(color === 'transparent' ? 'transparent' : color)
                    }
                    className={`w-6 h-6 rounded-sm border transition-all hover:scale-110 ${
                      fill.color === color
                        ? 'border-cyan-500 ring-1 ring-cyan-500/50'
                        : 'border-white/[0.1]'
                    }`}
                    style={{
                      background:
                        color === 'transparent'
                          ? 'repeating-conic-gradient(#808080 0% 25%, transparent 0% 50%) 50% / 6px 6px'
                          : color,
                    }}
                    title={color === 'transparent' ? 'Transparent' : color}
                  />
                ))}
              </div>
            </>
          ) : (
            <>
              {fill.gradient && (
                <>
                  <div
                    className="w-full h-8 rounded-md mb-3 border border-white/[0.1]"
                    style={{ background: previewBackground }}
                  />

                  <div className="flex items-center gap-2 mb-3">
                    <button
                      type="button"
                      onClick={handleGradientTypeToggle}
                      className="text-[10px] px-2 py-1 bg-white/[0.03] border border-white/[0.06] rounded text-white/60 hover:text-white/80 transition-colors"
                    >
                      {fill.gradient.type === 'linear' ? 'Linear' : 'Radial'}
                    </button>
                    {fill.gradient.type === 'linear' && (
                      <div className="flex items-center gap-1 flex-1">
                        <span className="text-[10px] text-white/30">Angle</span>
                        <input
                          type="number"
                          value={fill.gradient.angle}
                          onChange={(e) =>
                            handleGradientAngleChange(parseInt(e.target.value) || 0)
                          }
                          min={0}
                          max={360}
                          className="w-14 px-1.5 py-0.5 text-xs bg-[#12121a] border border-white/[0.06] rounded text-white text-center focus:outline-none focus:border-cyan-500/50"
                        />
                        <span className="text-[10px] text-white/30">deg</span>
                      </div>
                    )}
                  </div>

                  <div className="space-y-2 mb-2">
                    {fill.gradient.stops.map((stop, index) => (
                      <div key={index} className="flex items-center gap-1.5">
                        <input
                          type="color"
                          value={stop.color}
                          onChange={(e) =>
                            handleGradientStopColorChange(index, e.target.value)
                          }
                          className="w-6 h-6 rounded cursor-pointer border-0 bg-transparent flex-shrink-0"
                        />
                        <input
                          type="text"
                          value={stop.color}
                          onChange={(e) =>
                            handleGradientStopColorChange(index, e.target.value)
                          }
                          className="flex-1 px-1.5 py-0.5 text-[10px] font-mono bg-[#12121a] border border-white/[0.06] rounded text-white focus:outline-none focus:border-cyan-500/50"
                        />
                        <input
                          type="number"
                          value={Math.round(stop.offset * 100)}
                          onChange={(e) =>
                            handleGradientStopOffsetChange(
                              index,
                              parseInt(e.target.value) / 100
                            )
                          }
                          min={0}
                          max={100}
                          className="w-12 px-1 py-0.5 text-[10px] bg-[#12121a] border border-white/[0.06] rounded text-white text-center focus:outline-none focus:border-cyan-500/50"
                        />
                        <span className="text-[10px] text-white/20">%</span>
                        {fill.gradient!.stops.length > 2 && (
                          <button
                            type="button"
                            onClick={() => removeGradientStop(index)}
                            className="text-white/20 hover:text-red-400 text-xs transition-colors"
                          >
                            x
                          </button>
                        )}
                      </div>
                    ))}
                  </div>

                  <button
                    type="button"
                    onClick={addGradientStop}
                    className="w-full text-[10px] py-1 text-white/40 hover:text-cyan-400 border border-dashed border-white/[0.06] rounded hover:border-cyan-500/30 transition-colors"
                  >
                    + Add Stop
                  </button>
                </>
              )}
            </>
          )}

          {showOpacity && (
            <div className="mt-3 pt-3 border-t border-white/[0.06]">
              <div className="flex items-center justify-between mb-1">
                <span className="text-[10px] text-white/30">Opacity</span>
                <span className="text-[10px] text-white/50 font-mono">
                  {Math.round((opacity ?? 1) * 100)}%
                </span>
              </div>
              <input
                type="range"
                min={0}
                max={100}
                value={Math.round((opacity ?? 1) * 100)}
                onChange={(e) => onOpacityChange?.(parseInt(e.target.value) / 100)}
                className="w-full h-1.5 bg-white/[0.06] rounded-full appearance-none cursor-pointer
                  [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-3 [&::-webkit-slider-thumb]:h-3
                  [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-cyan-500 [&::-webkit-slider-thumb]:border-2
                  [&::-webkit-slider-thumb]:border-white/20 [&::-webkit-slider-thumb]:shadow-lg"
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
}
